import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { Label } from 'src/app/labels/englishLabels';

import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { IPageFilters } from 'src/app/models/filters/interfaces/pageFilters.interface';
import { ISelectOption } from 'src/app/models/selectOptions';

@Component({
  selector: 'app-autocomplete-api',
  templateUrl: './autocomplete-api.component.html',
  styleUrls: ['./autocomplete-api.component.scss'],
})
export class AutocompleteApiComponent implements OnInit {
  @Input() public pageFilters: IPageFilters;
  @Input() public searchValuesCtrl = new UntypedFormControl();
  @Input() public filteredValues: ISelectOption[];
  @Input() public isLoading$: BehaviorSubject<boolean>;
  @Input() public filterType: FilterTypesEnum;
  @Input() public maxSelectedItems?: number;

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  public allValues$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public selectedIds: Set<string>;
  public maxSelectedItemMessage: string;

  ngOnInit() {
    this.pageFilters.getAllFiltersOptions().subscribe((options) => {
      const previouslySelectedOptions: ISelectOption[] = this.allValues$.value;
      options.forEach((option) => {
        if (option.categoryName === this.filterType && !this.allValues$.value.includes(option)) {
          previouslySelectedOptions.push(option);
        }
      });
      this.allValues$.next(previouslySelectedOptions);
    });

    this.pageFilters.onUpdate$.subscribe(() => {
      const newSelectedIds: Set<string> = new Set();
      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        if (option.categoryName === this.filterType) {
          newSelectedIds.add(option.id);
        }
      });
      this.selectedIds = newSelectedIds;
    });

    if (this.maxSelectedItems !== undefined) {
      this.maxSelectedItemMessage = Label.get('common.maximumItemsSelectionReached').replace(
        '{0}',
        this.maxSelectedItems
      );
    }
  }

  public displayWith(value: ISelectOption): string {
    return value === null ? value : value.displayName;
  }

  public optionClicked(event: Event, value: ISelectOption) {
    if (this.isOptionDisabled(value)) {
      event.stopPropagation();
      return;
    }
    this.toggleSelection(value);
    event.stopPropagation();
  }

  public toggleSelection(selectedOption: ISelectOption) {
    const selectedOptionIsNew = !this.allValues$.value.includes(selectedOption);
    const _isMaxItemsReached = this.isMaxReached();

    if (!this.selectedIds.has(selectedOption.id)) {
      if (!_isMaxItemsReached) {
        this.selectedIds.add(selectedOption.id);
      }
    } else {
      this.selectedIds.delete(selectedOption.id);
      this.clearSingleOption(selectedOption);
    }

    const allOptions =
      selectedOptionIsNew && !_isMaxItemsReached
        ? _.union(this.allValues$.value, [selectedOption])
        : this.allValues$.value;
    this.allValues$.next(allOptions);

    this.searchValuesCtrl.setValue(this.selectedIds);
    this.optionOnSelection.emit(allOptions.filter((option) => this.selectedIds.has(option.id)));
  }

  public clearSingleOption(optionToRemove: ISelectOption) {
    this.pageFilters.removeSelectedOptionsIfExist([optionToRemove]);
  }

  public isOptionDisabled(value: ISelectOption): boolean {
    return (
      this.maxSelectedItems !== undefined &&
      this.maxSelectedItems === this.selectedIds.size &&
      !this.selectedIds.has(value.id)
    );
  }

  public isMaxReached(): boolean {
    return this.maxSelectedItems !== undefined && this.selectedIds.size === this.maxSelectedItems;
  }
}
